import React, { useState, useEffect } from 'react';
import { oracleDeck, uiText } from '../../data/oracle/oracleDeckData';
import { useGlow } from '../../hooks/useGlow';
import { LockClosedIcon, KeyIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { getOracleReading } from '../../services/geminiService'; 
import AIReadingDisplay from './AIReadingDisplay'; 
import OracleCreditManager from './OracleCreditManager';
import CooldownTimer from '../common/CooldownTimer';

const UNLOCK_CODE = "PORTALNUEVO"; 
const STORAGE_KEY = "threeCardOracleDaily";

// --- SUBCOMPONENTE: Carta Individual Actualizado ---
const CardSlot = ({ card, isReversed, isRevealed, onClick, positionLabel }) => {
  return (
    <div className="flex flex-col items-center gap-4">
      {/* Etiqueta de posición (Situación, Obstáculo, etc) */}
      <span className="text-purple-300 text-sm font-bold tracking-widest uppercase opacity-80 h-6">
        {positionLabel}
      </span>

      {/* Contenedor de la Carta (Flip Animation) */}
      <div 
        onClick={!isRevealed ? onClick : undefined}
        className={`relative w-44 h-64 sm:w-48 sm:h-72 cursor-pointer perspective-1000 group ${!isRevealed ? 'hover:-translate-y-2' : ''} transition-transform duration-300`}
      >
        <div className={`relative w-full h-full duration-700 preserve-3d transition-transform ${isRevealed ? 'rotate-y-180' : ''}`}>
          {/* REVERSO */}
          <div className="absolute inset-0 backface-hidden w-full h-full">
            <img
              src={oracleDeck[0].cardBack}
              alt="Reverso"
              className="w-full h-full object-cover rounded-xl border-2 border-purple-400/30 shadow-lg shadow-purple-900/40"
            />
          </div>
          {/* FRENTE */}
          <div className="absolute inset-0 backface-hidden w-full h-full rotate-y-180 bg-gray-900 rounded-xl overflow-hidden border-2 border-sky-400/50 shadow-sky-500/20 shadow-2xl">
            <img
              src={card.image}
              alt={card.data.name}
              className={`w-full h-full object-cover transition-transform duration-700 ${isReversed ? 'rotate-180' : ''}`}
            />
            {isReversed && <div className="absolute inset-0 bg-indigo-900/10 pointer-events-none mix-blend-overlay"></div>}
          </div>
        </div>
      </div>

      {/* TEXTOS DE LA CARTA (Se muestran al revelar) */}
      <div className={`text-center transition-all duration-700 flex flex-col items-center gap-1 ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
        
        {/* Categoría: Pequeña y destacada */}
        <span className="text-sky-400 text-[10px] font-bold tracking-widest uppercase bg-sky-950/30 px-2 py-0.5 rounded border border-sky-500/20">
            {card.data.category}
        </span>

        {/* Nombre Principal */}
        <h3 className="text-gray-100 font-bold text-xl leading-tight uppercase tracking-tight">
            {card.data.name}
        </h3>

        {/* Nombres Secundarios / Keywords */}
        <p className="text-gray-400 text-xs italic font-medium max-w-[180px]">
            {card.data.name2} <span className="text-gray-600 mx-1">•</span> {card.data.name3}
        </p>

        {/* Etiqueta de Invertida */}
        {isReversed && (
            <span className="text-red-400 text-[10px] uppercase font-black tracking-tighter border border-red-400/30 px-2 py-0.5 rounded-full mt-1 bg-red-900/20">
                Invertida
            </span>
        )}
      </div>
    </div>
  );
};

// --- COMPONENTE PRINCIPAL ---
const ThreeCardOracleSpread = ({ lang = 'es' }) => {
  const [cards, setCards] = useState([]); 
  const [topic, setTopic] = useState('');
  const [revealedCount, setRevealedCount] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  
  const [showUnlockInput, setShowUnlockInput] = useState(false);
  const [unlockCode, setUnlockCode] = useState('');
  const [unlockError, setUnlockError] = useState('');

  const [aiReading, setAiReading] = useState('');
  const [isLoadingAi, setIsLoadingAi] = useState(false);
  const [storedDate, setStoredDate] = useState(null);

  const ui = uiText[lang] || uiText['es'];
  
  const localUi = {
    spreadTitle: lang === 'en' ? 'Three Card Reading' : 'Lectura de Tres Cartas',
    spreadIntro: lang === 'en' ? 'What is on your mind? Write your topic below to unlock the cards.' : '¿Qué tienes en mente? Escribe tu tema abajo para desbloquear las cartas.',
    inputPlaceholder: lang === 'en' ? 'Ex: My spiritual growth, My relationship...' : 'Ej: Mi crecimiento, Mi relación, Trabajo...',
    pos1: lang === 'en' ? '1. The Situation' : '1. La Situación',
    pos2: lang === 'en' ? '2. The Obstacle' : '2. El Obstáculo',
    pos3: lang === 'en' ? '3. The Advice' : '3. El Consejo',
    contactBtn: lang === 'en' ? 'Get Full Interpretation' : 'Solicitar Interpretación Humana',
    aiBtn: lang === 'en' ? 'Ask the Oracle (AI)' : 'Pedir Interpretación al Oráculo (IA)',
    reset: lang === 'en' ? 'Unlock New Reading' : 'Desbloquear Nueva Tirada',
    codePlaceholder: lang === 'en' ? 'Enter Code' : 'Ingresa el código...',
    dailyLimitMsg: lang === 'en' ? 'You have already done your reading for today.' : 'Ya has realizado tu lectura de hoy.'
  };

  const titleStyle = useGlow({ gradient: 'linear-gradient(to bottom, #e0e0e0ff, #9b977aff, #9e823cff, #776f5cff)' });
  const topicStyle = useGlow({ gradient: 'linear-gradient(to bottom, #e0e0e0ff, #7a9b9bff, #3c779eff, #5d5c77ff)' });

  // EFECTO: Cargar desde localStorage al iniciar
  useEffect(() => {
    const checkLocalStorage = () => {
      try {
        const storedData = localStorage.getItem(STORAGE_KEY);

        if (storedData) {
          const {
            date,
            cards: savedCards,
            topic: savedTopic,
            reading: savedReading
          } = JSON.parse(storedData);

          const lastReadingDate = new Date(date);
          const today = new Date();

          setStoredDate(lastReadingDate);

          const diffInDays = Math.floor(
            (today - lastReadingDate) / (1000 * 60 * 60 * 24)
          );

          // Solo restaurar si todavía NO pasaron 3 días
          if (diffInDays < 3) {
            const restoredCards = savedCards.map(saved => {
              const fullCard = oracleDeck.find(c => c.id === saved.id);

              return {
                data: fullCard[lang],
                image: fullCard.image,
                originalId: fullCard.id,
                isReversed: saved.isReversed,
                isRevealed: true
              };
            });

            setCards(restoredCards);
            setTopic(savedTopic || '');
            setRevealedCount(3);

            if (savedReading) {
              setAiReading(savedReading.text);
            }
          } else {
            // Ya pasaron 3 días: limpiar la lectura anterior
            localStorage.removeItem(STORAGE_KEY);

            setCards([]);
            setTopic('');
            setAiReading('');
            setRevealedCount(0);
          }
        }
      } catch (error) {
        console.error("Error reading localStorage:", error);
      }
    };

    checkLocalStorage();
  }, [lang]);

  const handleStartSpread = () => {
    if (isDrawing || !topic.trim()) return;
    
    setIsDrawing(true);
    setAiReading('');

    setTimeout(() => {
      const deckCopy = [...oracleDeck];
      const selected = [];
      const today = new Date().toISOString().split('T')[0];

      for (let i = 0; i < 3; i++) {
        const randomIndex = Math.floor(Math.random() * deckCopy.length);
        const cardData = deckCopy[randomIndex];
        const inversionRules = [true, true, deckCopy[randomIndex].allowInvertedAdvice]; 
        const canBeReversed = inversionRules[i];
        const isReversed = canBeReversed ? Math.random() < 0.5 : false;
        
        selected.push({
          data: cardData[lang],
          image: cardData.image,
          originalId: cardData.id,
          isReversed: isReversed,
          isRevealed: false
        });
        
        deckCopy.splice(randomIndex, 1);
      }

      setCards(selected);
      setRevealedCount(0);
      setIsDrawing(false);

      const dataToSave = {
        date: today,
        topic: topic,
        cards: selected.map(c => ({ id: c.originalId, isReversed: c.isReversed })),
        reading: '' // Inicialmente vacía
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));

    }, 1500);
  };

  const handleRevealCard = (index) => {
    const newCards = [...cards];
    if (!newCards[index].isRevealed) {
      newCards[index].isRevealed = true;
      setCards(newCards);
      setRevealedCount(prev => prev + 1);
    }
  };

  const handleUnlockReset = () => {
    if (unlockCode.toUpperCase() === UNLOCK_CODE) {
        localStorage.removeItem(STORAGE_KEY);
        setCards([]);
        setTopic('');
        setRevealedCount(0);
        setAiReading('');
        setShowUnlockInput(false);
        setUnlockCode('');
        setUnlockError('');
    } else {
        setUnlockError('Código incorrecto');
    }
  };

  // --- LÓGICA DE IA CON GUARDADO EN LOCALSTORAGE ---
  const handleGetAIReading = async () => {
    if (cards.length < 3) return;
    setIsLoadingAi(true);
    
    const cardsForAi = cards.map((c, index) => ({
        name: `${c.data.name} ${c.data.name2} ${c.data.name3} - ${c.data.category}`,
        positionName: index === 0 ? localUi.pos1 : index === 1 ? localUi.pos2 : localUi.pos3,
        isReversed: c.isReversed,
        breaf: c.data.breaf || c.data.description,
        keywords: c.data.keywords
    }));

    const reading = await getOracleReading(topic, localUi.spreadTitle, cardsForAi, lang);
    
    if (reading.success) {
      setAiReading(reading.text);

      // Actualiza el timer inmediatamente
      const now = new Date().toISOString();
      setStoredDate(now);
    }
    
    setIsLoadingAi(false);

    // GUARDAR EN LOCALSTORAGE
    try {
        const storedData = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        // Solo actualizamos si el objeto es válido y tiene fecha (para no corromper)
        if (storedData.date) {
            const updatedData = { ...storedData, reading: reading };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));
        }
    } catch (e) {
        console.error("Error guardando lectura en LS", e);
    }
  };

  const generateContactLink = () => {
    const cardNames = cards.map(c => `${c.data.name} (${c.isReversed ? 'Invertida' : 'Al derecho'})`).join(', ');
    const message = `Hola, hice una tirada sobre el tema: "${topic}". Cartas: ${cardNames}. ¿Me das una sesión personalizada?`;
    return `https://wa.me/5491150254270?text=${encodeURIComponent(message)}`; 
  };

  if (cards.length === 0) {
    const isInputValid = topic.trim().length > 0;
    return (
      <div className="flex flex-col items-center justify-center text-center p-8 min-h-[70vh] animate-fade-in w-full max-w-4xl mx-auto">
        <h1 style={titleStyle} className="text-4xl md:text-5xl font-bold mb-4">{localUi.spreadTitle}</h1>
        <p className="text-lg text-gray-400 mb-8 max-w-md mx-auto">{localUi.spreadIntro}</p>
        
        <div className="relative w-full max-w-sm mb-8 group z-20">
            <div className={`absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-sky-600 rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 ${isInputValid ? 'opacity-100' : 'opacity-50'}`}></div>
            <input 
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder={localUi.inputPlaceholder}
                className="relative w-full bg-gray-900 text-white rounded-lg p-4 text-center border border-gray-700 focus:outline-none focus:bg-gray-800 placeholder-gray-500"
            />
        </div>

        <div className="relative">
            <button
            onClick={handleStartSpread}
            disabled={isDrawing || !isInputValid}
            className={`relative group w-48 h-64 perspective-1000 transition-all duration-500 ${!isInputValid ? 'cursor-not-allowed opacity-50 grayscale' : 'cursor-pointer'}`}
            >
            {[...Array(5)].map((_, i) => (
                <img
                key={i}
                src={oracleDeck[0].cardBack}
                alt="Mazo"
                className={`absolute inset-0 w-full h-full object-cover rounded-xl border-2 border-purple-400/30 shadow-lg
                            transition-all duration-500 ease-in-out
                            ${isDrawing ? 'animate-pulse' : isInputValid ? 'group-hover:-translate-y-4 shadow-purple-500/50' : ''}`}
                style={{
                    transform: `rotateZ(${i * 2 - 4}deg) translateY(${i * -1}px)`,
                    zIndex: 5 - i,
                }}
                />
            ))}
            {!isInputValid && (
                <div className="absolute inset-0 flex items-center justify-center z-20">
                    <LockClosedIcon className="w-16 h-16 text-gray-400 opacity-80" />
                </div>
            )}
            </button>
        </div>

        <p className={`mt-10 font-semibold transition-colors duration-300 ${isInputValid ? 'text-sky-300 animate-pulse' : 'text-gray-600'}`}>
          {isDrawing ? "Conectando con el oráculo..." : isInputValid ? "Toca el mazo para revelar" : "Escribe tu pregunta primero"}
        </p>
      </div>
    );
  }

  const allRevealed = revealedCount === 3;

  return (
    <div className="w-full max-w-6xl mx-auto p-4 md:p-8 min-h-[80vh] flex flex-col items-center">
      
      <div className="text-center mb-10 animate-fade-in-down">
        <span style={titleStyle} className="text-sm font-bold uppercase tracking-[0.2em] text-purple-400 mb-2 block">
            {localUi.spreadTitle}
        </span>
        <h2 style={topicStyle} className="text-2xl md:text-3xl font-bold text-white max-w-2xl mx-auto">
            "{topic}"
        </h2>
        {allRevealed && (
             <p className="text-xs text-gray-500 mt-2">{localUi.dailyLimitMsg}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-12 w-full place-items-center">
        {cards.map((card, idx) => (
             <CardSlot 
                key={idx}
                card={card} 
                isReversed={card.isReversed} 
                isRevealed={card.isRevealed} 
                onClick={() => handleRevealCard(idx)}
                positionLabel={idx === 0 ? localUi.pos1 : idx === 1 ? localUi.pos2 : localUi.pos3}
            />
        ))}
      </div>

      <div className={`w-full max-w-3xl flex flex-col items-center gap-8 transition-all duration-1000 ${allRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}>
        
        <div className="w-full">
            {!aiReading && !isLoadingAi ? (
                <div className="flex justify-center">
                    <button 
                        onClick={handleGetAIReading}
                        className="bg-purple-600 hover:bg-purple-500 text-white font-semibold py-3 px-8 rounded-full shadow-lg shadow-purple-900/50 transition-all flex items-center gap-2 animate-bounce-slow"
                    >
                        <SparklesIcon className="w-5 h-5" />
                        {localUi.aiBtn}
                    </button>
                </div>
            ) : (
                <AIReadingDisplay 
                    interpretation={aiReading} 
                    isLoading={isLoadingAi} 
                    topic={topic}
                    cards={cards.map((c, i) => ({
                        ...c, 
                        positionName: i === 0 ? localUi.pos1 : i === 1 ? localUi.pos2 : localUi.pos3
                    }))}
                    spreadName={localUi.spreadTitle}
                />
            )}
        </div>

        <a 
            href={generateContactLink()}
            target="_blank" 
            rel="noopener noreferrer"
            className="py-4 px-10 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white rounded-full font-bold text-lg transition-all duration-300 shadow-lg shadow-emerald-900/50 hover:scale-105 hover:shadow-emerald-500/40 flex items-center gap-2"
        >
            {localUi.contactBtn}
        </a>

        <CooldownTimer
          startDate={storedDate}
          daysLimit={3}
          lang={lang}
        />

        <div className="mt-8 pt-8 border-t border-gray-800 w-full flex flex-col items-center">

            <OracleCreditManager 
                lang={lang}
                onCreditUsed={() => {
                    // Esta función se dispara DESPUÉS de que el servidor confirmó el consumo
                    localStorage.removeItem(STORAGE_KEY);
                    setCards([]);
                    setTopic('');
                    setRevealedCount(0);
                    setAiReading('');
                }}
            />
        </div>
      </div>
    </div>
  );
};

export default ThreeCardOracleSpread;